import React, { useEffect, useState } from "react";
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  Box, 
  Typography, 
  Button, 
  IconButton,
  CircularProgress,
  DialogContentText,
  DialogActions,
  LinearProgress,
  TextField,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Grid
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import BarChartIcon from "@mui/icons-material/BarChart";
import { format } from "date-fns";
import { 
  useGetSubscriptionDetailsQuery, 
  useGetBillingHistoryQuery,
  useCancelSubscriptionMutation,
  useCreatePlanSubscriptionMutation,
  useReactivateSubscriptionMutation
} from "../../store/userApi";
import { useSelector } from "react-redux";
import { RootState } from "../../store/store";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import SubscriptionFormDirect from "./SubscriptionFormDirect";
import PaymentMethodUpdateDialog from "./PaymentMethodUpdateDialog";
import CancelSubscriptionDialog from "./CancelSubscriptionDialog";
import { getErrorMessage } from "../../error-helpers/errorMessage";
import { showErrorToast, showSuccessToast } from "../../toastUtils";

// Transcription limit constant (configurable via environment variable)
const TRANSCRIPTION_LIMIT = parseInt(process.env.REACT_APP_FREE_TRANSCRIPTION_LIMIT || '5', 10);

// Get Stripe publishable key from environment
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || "");
const planId = process.env.REACT_APP_STRIPE_PRICE_ID || "";
const planPrice = process.env.REACT_APP_STRIPE_PRICE_AMOUNT ? Number(process.env.REACT_APP_STRIPE_PRICE_AMOUNT) : 0;


interface SubscriptionModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  goToPaymentPage?: boolean;
}

const SubscriptionModal: React.FC<SubscriptionModalProps> = ({ open, onClose, onSuccess, goToPaymentPage }) => {
  const [openPaymentDialog, setOpenPaymentDialog] = useState(false);
  const [openCancelDialog, setOpenCancelDialog] = useState(false);
  const navigate = useNavigate();
  
  const user = useSelector((state: RootState) => state.user.user);
  const { data: subscriptionDetails, isLoading: isLoadingSubscription } = useGetSubscriptionDetailsQuery(undefined, {
    skip: !open || !user?.hasActiveSubscription
  });
  
  const { data: billingHistory, isLoading: isLoadingHistory } = useGetBillingHistoryQuery(undefined, {
    skip: !open
  });

  const [reactivateSubscription, { isLoading: isReactivating }] = useReactivateSubscriptionMutation();

  const [step, setStep] = useState<"info" | "form">("info");
  const [loading, setLoading] = useState(false);
  
  // Determine if user is eligible for trial period
  const isEligibleForTrial = !user?.hadSubscriptionBefore;

  const handleOpenPaymentDialog = () => {
    setOpenPaymentDialog(true);
  };

  const handleClosePaymentDialog = () => {
    setOpenPaymentDialog(false);
  };

  const handleOpenCancelDialog = () => {
    setOpenCancelDialog(true);
  };

  const handleCloseCancelDialog = (wasCancelled = false) => {
    setOpenCancelDialog(false);
    // If subscription was cancelled, close the modal completely
    if (wasCancelled) {
      handleClose();
    }
  };

  const handleProceedToSubscription = () => {
    setStep("form");
  };

  const handleSubscriptionSuccess = () => {
    setLoading(false);
    if (onSuccess) {
      onSuccess();
    }
    handleClose();
  };

  const handleBack = () => {
    setStep("info");
  };

  const handleCancel = () => {
    setStep("info");
    handleClose();
  };

  // Initialize with payment form if goToPaymentPage is true
  // This runs only once when the component mounts
  useEffect(() => {
    // Only go to payment page if specifically requested AND user doesn't have an active subscription
    // This ensures the modal always opens on "info" by default
    if (goToPaymentPage && !user?.hasActiveSubscription) {
      setStep("form");
    } else {
      setStep("info");
    }
  // We intentionally only run this once at mount to capture the initial value of goToPaymentPage
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]); // Add 'open' as a dependency to reset the step each time the modal opens

  // Enhanced close handler that resets state
  const handleClose = () => {
    setStep("info"); // Reset to info step
    onClose(); // Call the original onClose prop
  };

  // Calculate remaining transcriptions using the value from user object
  const usedTranscriptions = user?.transcriptionCount || 0;
  const remainingTranscriptions = TRANSCRIPTION_LIMIT - usedTranscriptions;
  const transcriptionPercentage = (usedTranscriptions / TRANSCRIPTION_LIMIT) * 100;

  // Add new function to handle subscription reactivation
  const handleReactivateSubscription = async () => {
    setLoading(true);
    try {
      await reactivateSubscription().unwrap();
      showSuccessToast("Your subscription has been reactivated and will renew automatically");
      setLoading(false);
      // Close the modal after successful reactivation
      handleClose();
    } catch (error) {
      showErrorToast(getErrorMessage(error) || "Failed to reactivate subscription");
      setLoading(false);
    }
  };

  // Check if subscription is already cancelled but still active (won't renew)
  const isSubscriptionCancelled = subscriptionDetails?.cancelAtPeriodEnd === true;

  return (
    <>
      <Dialog 
        open={open} 
        onClose={loading ? undefined : handleClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "12px",
            backgroundColor: "#1a1f2e",
            color: "#ffffff"
          }
        }}
      >
        {step === "info" ? (
          <>
            <DialogTitle 
              sx={{ 
                display: "flex", 
                justifyContent: "space-between", 
                alignItems: "center",
                borderBottom: "1px solid rgba(255,255,255,0.1)",
                padding: "20px 24px",
              }}
            >
              <Typography
                component="div"
                sx={{ fontWeight: "600", fontSize: "1.5rem" }}
              >
                Subscription Settings
              </Typography>
              <IconButton onClick={handleClose} sx={{ color: "#ffffff" }}>
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ padding: 0 }}>
              {/* Current Plan Section */}
              <Box
                sx={{
                  padding: "24px",
                  borderBottom: "1px solid rgba(255,255,255,0.1)",
                }}
              >
                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: "500" }}>
                    Current Plan
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: "600", color: "#3b82f6" }}>
                    {user?.hasActiveSubscription ? "Pro" : "Personal"}
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.7)" }}>
                  {user?.hasActiveSubscription && subscriptionDetails ? (
                    <>
                      {isSubscriptionCancelled
                        ? `Your subscription ends on ${format(new Date(subscriptionDetails.currentPeriodEnd), "MMMM d, yyyy")} and will not renew.`
                        : `Your plan renews on ${format(new Date(subscriptionDetails.currentPeriodEnd), "MMMM d, yyyy")}${
                            subscriptionDetails.status === 'trialing' ? 
                            ` (Currently in trial period until ${format(new Date(subscriptionDetails.currentPeriodEnd), "MMMM d, yyyy")})` : 
                            ""
                          }`
                      }
                    </>
                  ) : (
                    `Free plan - ${process.env.REACT_APP_FREE_TRANSCRIPTION_LIMIT || '5'} transcriptions limit`
                  )}
                </Typography>
                
                {/* Display Transcription Usage for Personal plan */}
                {!user?.hasActiveSubscription && (
                  <Box mt={2}>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                      <BarChartIcon sx={{ mr: 1, color: "#3b82f6" }} />
                      <Typography variant="body1" sx={{ fontWeight: "500" }}>
                        Transcription Usage
                      </Typography>
                    </Box>
                    
                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                      <Typography variant="body2">
                        Used: {usedTranscriptions} of {TRANSCRIPTION_LIMIT}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: "500", color: remainingTranscriptions > 5 ? "#4ade80" : "#ef4444" }}>
                        {remainingTranscriptions} remaining
                      </Typography>
                    </Box>
                    <LinearProgress 
                      variant="determinate" 
                      value={transcriptionPercentage}
                      sx={{ 
                        height: 8, 
                        borderRadius: 4,
                        mb: 2,
                        backgroundColor: 'rgba(255,255,255,0.1)',
                        '& .MuiLinearProgress-bar': {
                          backgroundColor: remainingTranscriptions > 5 ? '#4ade80' : '#ef4444'
                        }
                      }} 
                    />
                    
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleProceedToSubscription}
                      sx={{ textTransform: "none", mt: 1 }}
                    >
                      Upgrade to Pro
                    </Button>
                  </Box>
                )}
              </Box>

              {/* Payment Method Section */}
              <Box
                sx={{
                  padding: "24px",
                  borderBottom: "1px solid rgba(255,255,255,0.1)",
                }}
              >
                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: "500" }}>
                    Payment Method
                  </Typography>
                  {user?.hasActiveSubscription && !isSubscriptionCancelled && (
                    <Button
                      variant="text"
                      sx={{ color: "#3b82f6" }}
                      onClick={handleOpenPaymentDialog}
                    >
                      Update
                    </Button>
                  )}
                </Box>
                {user?.hasActiveSubscription ? (
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <CreditCardIcon sx={{ mr: 2 }} />
                    <Box>
                      <Typography variant="body1">
                        •••• 4242 {/* Typically we'd get this from Stripe */}
                      </Typography>
                      <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.7)" }}>
                        Expires 12/24 {/* Also would come from Stripe */}
                      </Typography>
                    </Box>
                  </Box>
                ) : (
                  <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.7)" }}>
                    No payment method on file
                  </Typography>
                )}
              </Box>

              {/* Billing History Section */}
              <Box sx={{ padding: "24px" }}>
                <Typography variant="h6" sx={{ fontWeight: "500", mb: 2 }}>
                  Billing History
                </Typography>
                
                {isLoadingHistory ? (
                  <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : billingHistory && billingHistory.length > 0 ? (
                  billingHistory.map((item, index) => (
                    <Box 
                      key={item.id}
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "16px 0",
                        borderBottom: index < billingHistory.length - 1 ? "1px solid rgba(255,255,255,0.1)" : "none",
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <CalendarTodayIcon sx={{ mr: 2 }} />
                        <Box>
                          <Typography variant="body1">
                            {format(new Date(item.created * 1000), "MMMM d, yyyy")}
                          </Typography>
                          <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.7)" }}>
                            Pro Plan - Monthly
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Typography variant="body1" sx={{ fontWeight: "500", mr: 1 }}>
                          ${(item.amount / 100).toFixed(2)}
                        </Typography>
                        <ArrowForwardIcon fontSize="small" sx={{ color: "rgba(255,255,255,0.5)" }} />
                      </Box>
                    </Box>
                  ))
                ) : (
                  <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.7)" }}>
                    No billing history available
                  </Typography>
                )}
              </Box>

              {/* Cancel/Reactivate Subscription Button */}
              {user?.hasActiveSubscription && (
                <Box sx={{ padding: "0 24px 24px 24px", display: "flex", justifyContent: "center" }}>
                  {isSubscriptionCancelled ? (
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={handleReactivateSubscription}
                      disabled={isReactivating || loading}
                      sx={{ 
                        textTransform: "none", 
                        fontSize: "16px",
                      }}
                    >
                      {isReactivating || loading ? 
                        <CircularProgress size={24} color="inherit" /> : 
                        "Reactivate Subscription"
                      }
                    </Button>
                  ) : (
                    <Button
                      variant="text"
                      color="error"
                      onClick={handleOpenCancelDialog}
                      sx={{ 
                        textTransform: "none", 
                        fontSize: "16px",
                        color: "#ef4444"
                      }}
                    >
                      Cancel Subscription
                    </Button>
                  )}
                </Box>
              )}
            </DialogContent>
          </>
        ) : (
          <Box sx={{ p: 3 }}>
            <Elements stripe={stripePromise}>
              <SubscriptionFormDirect 
                planId={planId}
                planPrice={planPrice}
                onSuccess={handleSubscriptionSuccess}
                onCancel={handleBack}
                trialPeriodDays={0}
                isDirectSignup={false}
              />
            </Elements>
          </Box>
        )}
      </Dialog>

      {/* Payment Method Update Dialog */}
      <PaymentMethodUpdateDialog 
        open={openPaymentDialog} 
        onClose={handleClosePaymentDialog} 
      />

      {/* Cancel Subscription Dialog */}
      <CancelSubscriptionDialog 
        open={openCancelDialog} 
        onClose={handleCloseCancelDialog} 
      />
    </>
  );
};

export default SubscriptionModal; 